/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import clsx from "clsx";
import {ExclamationTriangleIcon, UserIcon} from "@heroicons/react/24/outline";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import dayjs from "dayjs";
import {DEFAULT_COUNTRY_CODE, MIN_PERSON_BIRTHDATE_AGE} from "helpers/config";
import {countryList, filteredSpecificCountryFirst, PhoneCountry} from "utils/countryUtils";
import {CountryCode, parsePhoneNumber} from "libphonenumber-js";
import BookingLoginAction from "store/bookingLogin/BookingLoginAction";
import Dates from "utils/Dates";
import {ReservationDriver} from "types/entities/ReservationDriver";
import BookingLoginReservationInfoCard
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationInfoCard";
import BookingLoginReservationButton
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginReservationButton";
import ReservationDetailInput
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailInput";
import ReservationDetailPhoneWithCountrySelect
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailPhoneWithCountrySelect";
import ReservationDetailCountry
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/ReservationDetailCountry";
import TableRowColumn from "pages/Front/Booking/BookingLogin/components/TableRowColumn";
import BookingLoginTableRow from "pages/Front/Booking/BookingLogin/components/BookingLoginTableRow";
import BookingLoginZuumeHeader
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/components/BookingLoginZuumeHeader";
import ReservationDataCollectionInfoCard
    from "pages/Front/Booking/BookingLogin/components/ReservationDataCollectionInfoCard";
import FeatherIcon from "components/FeatherIcon";
import Birthdate from "pages/Front/Registration/components/Birthdate";
import {PhoneNumberType, PhoneNumberUtil} from "google-libphonenumber";
import {InfoMessageType, setInfoMessage} from "components/InfoMessage";

export interface ReservationInvitationGuestFormInputs {
    reservation_driver_guid: string;
    name: string;
    last_name: string;
    nickname: string;
    birthdate: string;
    address: string;
    zip: string;
    town: string;
    country: string;
    email: string;
    phone: string;
}

const REGEX_EMAIL = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

interface Params {
    reservation_driver_guid: string;
}

interface BookingLoginReservationInvitationGuestProps extends RouteComponentProps<Params> {}

const BookingLoginReservationInvitationGuest = (props: BookingLoginReservationInvitationGuestProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const phoneUtil = PhoneNumberUtil.getInstance();
    const reservation_driver_guid = props.match.params.reservation_driver_guid;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [messageStatus, setMessageStatus] = useState<number | null>(null);
    const [reservationDriver, setReservationDriver] = useState<ReservationDriver | null>(null);
    const [reservationUnavailable, setReservationUnavailable] = useState(false);
    const successMessageRef = useRef<HTMLDivElement>(null);
    const maximumDate = dayjs().subtract(MIN_PERSON_BIRTHDATE_AGE, "year").toDate();
    const isChild = reservationDriver?.person_type === "child";
    const maximalAge = isChild ? 100 : 100;
    const minimalDate = dayjs().subtract(maximalAge, "year").toDate();

    const defaultCountry = countryList.find(item => item.textCode == DEFAULT_COUNTRY_CODE) ?? countryList[161];
    let driverPhonePrefix: string | number | undefined = 41;
    if (reservationDriver?.driver?.phone) {
        try {
            const phone = reservationDriver.driver.phone;
            const parsed = phone.startsWith("+") ? parsePhoneNumber(phone) : parsePhoneNumber(phone, DEFAULT_COUNTRY_CODE as CountryCode);
            driverPhonePrefix = parsed?.countryCallingCode;
        } catch (e) {
            console.error("Error parsing phone number", e);
        }
    }
    const defaultPhoneCountry = countryList.find(item => {
        if (Array.isArray(item.shortCode)) {
            return item.shortCode.includes(driverPhonePrefix as number);
        }
        return Number(item.shortCode) == driverPhonePrefix;
    });
    const propsDefaultCountry = countryList.find(item => item.name === reservationDriver?.driver?.country);
    const defaultCountryForSelect: PhoneCountry = propsDefaultCountry ? propsDefaultCountry : defaultCountry;
    const [selectedCountryForPhone, setSelectedCountryForPhone] = useState<PhoneCountry>(defaultPhoneCountry ?? defaultCountry);

    const ReservationInvitationGuestSchema = yup.object().shape({
        reservation_driver_guid: yup.string().required("Driver GUID is required."),
        name: yup.string().required(t("Firstname is required field.")),
        last_name: yup.string().required(t("Lastname is required field.")),
        nickname: yup.string().required(t("Nickname is required field.")),
        address: yup.string().required(t("Address is required field.")),
        zip: yup.string().required(t("Zip is required field.")),
        town: yup.string().required(t("Town is required field.")),
        country: yup.string().required(t("Country is required field.")),
        birthdate: yup
            .date()
            .required(t("Birthdate is required field."))
            .typeError(t("Birthdate is required field."))
            .min(minimalDate, t("maximal_person_age", {age: maximalAge}))
            .max(maximumDate, "Minimal person age is " + MIN_PERSON_BIRTHDATE_AGE + "."),
        email: yup.string().matches(REGEX_EMAIL, t("Invalid email format")).required(),
        phone: yup
            .string()
            .phone(selectedCountryForPhone?.textCode as CountryCode, t("Phone must be a valid phone number for region ") + selectedCountryForPhone?.name)
            .test("startsWithPlus", t("The phone number must start with the country code ") + selectedCountryForPhone?.code, function (value) {
                return (value || "").startsWith(selectedCountryForPhone?.code);
            })
            .test(
                "startsWithPlusAndGoogleVerify",
                t("Bad phone number format. The phone number must be mobile with international prefix ") + selectedCountryForPhone?.code,
                function (value) {
                    let phoneNumber;
                    try {
                        phoneNumber = phoneUtil.parseAndKeepRawInput(value, selectedCountryForPhone?.textCode);
                    } catch (err) {
                        return false;
                    }
                    return phoneUtil.isValidNumber(phoneNumber) && phoneUtil.getNumberType(phoneNumber) === PhoneNumberType.MOBILE;
                }
            )
            .test("mobileNumberBadFormatCH", t("Bad number format"), function (value) {
                if (selectedCountryForPhone?.textCode === "CH") {
                    return (value || "").startsWith(selectedCountryForPhone?.code + "7");
                }
                return true;
            })
            .required(t("Phone is required field.")),
    });
    const formDefaultValues = {
        reservation_driver_guid: reservation_driver_guid,
        name: reservationDriver?.driver?.name ?? "",
        //@AI fill for all reservationDriver.driver fields
        last_name: reservationDriver?.driver?.last_name ?? "",
        nickname: reservationDriver?.driver?.nickname ?? "",
        birthdate: reservationDriver?.driver?.birthdate ?? "",
        address: reservationDriver?.driver?.address ?? "",
        zip: reservationDriver?.driver?.zip ?? "",
        town: reservationDriver?.driver?.town ?? "",
        country: reservationDriver?.driver?.country ?? defaultCountryForSelect.name,
        email: reservationDriver?.driver?.email ?? undefined,
        // phone: reservationDriver?.driver?.phone ?? "+41",
        phone: reservationDriver?.driver?.phone ?? undefined,
    };

    const {
        watch,
        register,
        handleSubmit,
        setValue,
        clearErrors,
        formState: {errors},
    } = useForm<ReservationInvitationGuestFormInputs>({
        resolver: yupResolver(ReservationInvitationGuestSchema),
        defaultValues: {...formDefaultValues},
    });
    const watchCountry = watch("country");

    const fetchReservationInvitationGuestHandler = () => {
        dispatch(BookingLoginAction.getReservationInvitationGuest(reservation_driver_guid))
            .then(
                r => {
                    return setReservationDriver(r);
                },
                reject => {
                    console.error(reject);
                    if (reject?.status === 404) {
                        setReservationUnavailable(true);
                    }
                }
            )
            .catch(error => {
                console.log(error);
            });
    };

    const setDefaultValues = (reservationDriver: ReservationDriver) => {
        // Assuming driver is an object in reservationDriver
        const {driver} = reservationDriver;

        // Use `useForm` hook to get form methods
        // Iterate over the keys of the `driver` object
        for (const key in driver) {
            // Use the form's setValue method to update each field with the corresponding value from the driver object
            const requiredFieldNames = [
                "reservation_driver_guid",
                "name",
                "last_name",
                "nickname",
                "address",
                "zip",
                "town",
                "country",
                "birthdate",
                "email",
                "phone",
            ];
            if (requiredFieldNames.includes(key) && driver[key]) {
                setValue(
                    key as
                        | "reservation_driver_guid"
                        | "name"
                        | "last_name"
                        | "nickname"
                        | "address"
                        | "zip"
                        | "town"
                        | "country"
                        | "birthdate"
                        | "email"
                        | "phone",
                    driver[key]
                );
            }
        }
    };

    const onErrorHandler = (dataErrors: any) => {
        console.log("onErrorHandler", dataErrors);
    };

    const onSubmitHandler = (data: ReservationInvitationGuestFormInputs) => {
        if (isLoading) {
            return true;
        }

        setIsLoading(true);

        const {birthdate, ...rest} = data;
        const newData = {
            ...rest,
            birthdate: Dates.ymd(birthdate, "YYYY-MM-DD"),
        };
        return dispatch(BookingLoginAction.fetchReservationInvitationGuest(newData))
            .then(response => {
                if (successMessageRef?.current) {
                    successMessageRef.current.scrollIntoView({behavior: "smooth"});
                }
                setInfoMessage(t("Your profile has been saved successfully."), InfoMessageType.INFO);
                return setMessageStatus(200);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const setPhone = value => {
        setValue("phone", value);
    };

    const birthdate = watch("birthdate");

    useEffect(() => {
        clearErrors("birthdate");
    }, [birthdate]);

    useEffect(() => {
        if (!reservationDriver) {
            fetchReservationInvitationGuestHandler();
        }
        if (reservationDriver) {
            setDefaultValues(reservationDriver);
        }
    }, [reservationDriver]);

    return (
        <div className={"BookingLoginReservationInvitationGuest w-full pb-24"}>
            {!reservationUnavailable && <div className={"w-full text-left flex flex-col gap-y-5 md:gap-y-12 items-start mb-8 sm:mb-16"}>
                <BookingLoginZuumeHeader title={t("Booking invitation")} />
                {reservationDriver?.reservation && <BookingLoginReservationInfoCard reservation={reservationDriver.reservation} />}
                <BookingLoginZuumeHeader title={t("Online checkin")} />
                <ReservationDataCollectionInfoCard />
            </div>}

            {reservationUnavailable ? (
                <CardComponent className="w-full border border-red-200 !p-0">
                    <div className="flex min-h-[360px] flex-col items-center justify-between gap-8 rounded-[10px] bg-red-50 px-6 py-8 text-center text-red-900 md:px-10 md:py-10" role="alert">
                        <div className="flex flex-col items-center gap-4">
                            <ExclamationTriangleIcon className="size-12 text-red-600" aria-hidden="true" />
                            <h2 className="text-xl font-semibold">{t("Reservation invitation unavailable")}</h2>
                            <p>{t("This reservation invitation is no longer available.")}</p>
                            <p>{t("Please contact the reservation contact person for assistance.")}</p>
                        </div>
                        <BookingLoginReservationButton className="w-full max-w-sm" onClick={() => props.history.push("/")}>
                            <span className="flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[10px] border border-arbo-red bg-arbo-red px-5 py-3 font-bold text-white">
                                {t("Homepage")}
                                <FeatherIcon name="arrow-right" strokeWidth={2} className="size-5" />
                            </span>
                        </BookingLoginReservationButton>
                    </div>
                </CardComponent>
            ) : (

            <CardComponent className={"w-full !p-0 border border-gray-200"}>
                <div
                    className={
                        "flex flex-row justify-between w-full px-3 py-3 font-bold border-b-2 border-gray-200 bg-gray-100/20 text-gray-900 drop-shadow-sm"
                    }
                >
                    <div className={"flex flex-row items-center"}>
                        <UserIcon className={clsx("w-5 h-5 mr-1 text-gray-600/70 drop-shadow-xl")} />
                        <h4 className={"font-medium text-gray-700"}>{t("Your personal data")}</h4>
                    </div>
                </div>
                <div className={"flex w-full"}>
                    <form autoComplete={"off"} className={"w-full"} onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                        <BookingLoginTableRow
                            className={
                                "ReservationDriverForm grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 !items-start gap-4 xl:gap-y-4 xl:gap-x-8 py-5 lg:py-6 px-6 lg:px-12 xl:px-20 pt-6 pb-6 md:pb-16"
                            }
                        >
                            <input {...register("reservation_driver_guid")} type={"hidden"} />

                            <ReservationDetailInput errors={errors} label={"First name"} name={"name"} register={register} required={true} />
                            <ReservationDetailInput errors={errors} label={"Last name"} name={"last_name"} register={register} required={true} />
                            <ReservationDetailInput errors={errors} label={"Nickname"} name={"nickname"} register={register} required={true} />

                            <Birthdate
                                defaultValues={formDefaultValues}
                                name={"birthdate"}
                                errors={errors}
                                register={register}
                                label={t("Birthdate")}
                                setValue={setValue}
                                required={true}
                            />

                            <ReservationDetailInput errors={errors} label={"Address"} name={"address"} register={register} required={true} />

                            <ReservationDetailInput errors={errors} label={"Zip"} name={"zip"} register={register} required={true} />

                            <ReservationDetailInput errors={errors} label={"Town"} name={"town"} register={register} required={true} />

                            <ReservationDetailCountry
                                defaultCountry={defaultCountryForSelect}
                                errors={errors}
                                label={"Country"}
                                name={"country"}
                                options={filteredSpecificCountryFirst(["CH", "LI", "DE", "AT", "FR", "IT"], countryList)}
                                register={register}
                                required={true}
                                setValue={setValue}
                                value={watchCountry}
                            />
                            <ReservationDetailInput errors={errors} label={"Email"} name={"email"} register={register} required={true} />
                            <ReservationDetailPhoneWithCountrySelect
                                defaultCountry={selectedCountryForPhone}
                                errors={errors}
                                label={t("Mobile phone")}
                                name={"phone"}
                                options={countryList}
                                register={register}
                                required={true}
                                setSelectedCountry={setSelectedCountryForPhone}
                                setValue={setPhone}
                                value={formDefaultValues?.phone}
                            />
                            <TableRowColumn
                                className={
                                    // "InvitationActions mt-4 self-end lg:mt-0 col-span-1 md:col-start-3 xl:col-start-3  flex flex-row items-center justify-end gap-x-2.5"
                                    "InvitationActions mt-8 self-end col-span-1 lg:col-start-2 xl:col-start-3 flex flex-row items-center justify-end"
                                }
                            >
                                <BookingLoginReservationButton
                                    className={"w-full mx-auto"}
                                    onClick={handleSubmit(onSubmitHandler, onErrorHandler)}
                                    disabled={messageStatus === 200}
                                >
                                    <div
                                        className={clsx(
                                            "border  font-bold text-xs md:text-lg rounded-[10px] flex flex-row items-center justify-center gap-x-2.5 min-h-[50px] py-[10px] px-[10px] w-full",
                                            messageStatus === 200 ? "bg-gray-100 text-gray-600 border-gray-600" : "border-arbo-red"
                                        )}
                                    >
                                        {/*<BookingLoginReservationButtonInner*/}
                                        {/*    className={"inline-flex"}*/}
                                        {/*    size={BookingLoginReservationButtonSize.Normal}*/}
                                        {/*    style={messageStatus === 200 ? BookingLoginReservationButtonStyle.Disabled : BookingLoginReservationButtonStyle.Green}*/}
                                        {/*>*/}
                                        <span>{messageStatus === 200 ? t("Saved") : t("Save")}</span>
                                        <FeatherIcon name={"arrow-right"} strokeWidth={2} className={"size-6 flex-none stroke-2"} />
                                        {/*</BookingLoginReservationButtonInner>*/}
                                    </div>
                                </BookingLoginReservationButton>
                            </TableRowColumn>
                        </BookingLoginTableRow>
                    </form>
                </div>
                {/*<div className={"flex h-20 justify-center border-t p-3"}>*/}
                {/*    <BookingLoginReservationButton onClick={handleSubmit(onSubmitHandler, onErrorHandler)}>*/}
                {/*        <BookingLoginReservationButtonInner*/}
                {/*            className={"inline-flex"}*/}
                {/*            size={BookingLoginReservationButtonSize.Normal}*/}
                {/*            style={messageStatus === 200 ? BookingLoginReservationButtonStyle.Disabled : BookingLoginReservationButtonStyle.Green}*/}
                {/*        >*/}
                {/*            <CheckIcon className={"w-[16px] h-[16px] flex-none"} />*/}
                {/*            <span className={"flex ml-2"}>{messageStatus === 200 ? t("Saved") : t("Save")}</span>*/}
                {/*        </BookingLoginReservationButtonInner>*/}
                {/*    </BookingLoginReservationButton>*/}
                {/*</div>*/}
            </CardComponent>
            )}

            {/*<div*/}
            {/*    ref={successMessageRef}*/}
            {/*    className={clsx(*/}
            {/*        "border border-dashed items-center justify-center font-medium rounded-md px-3 py-2  mt-6 bg-green-50 text-center text-lg",*/}
            {/*        messageStatus === 200 && "border-green-500 text-green-500",*/}
            {/*        messageStatus !== 200 && messageStatus !== null && "border-red-500 text-red-500",*/}
            {/*        messageStatus ? "flex" : "hidden"*/}
            {/*    )}*/}
            {/*>*/}
            {/*    <CheckBadgeIcon className={"min-w-7 grow-0 shrink-0 self-start min-h-7 w-7 h-7 text-green-500"} />*/}
            {/*    <span className={"ml-1"}>*/}
            {/*        {messageStatus === 200 && t("Your profile has been saved successfully.")}*/}
            {/*        {messageStatus === 404 && t("Error, please contact support.")}*/}
            {/*    </span>*/}
            {/*</div>*/}
        </div>
    );
};

export default withRouter(BookingLoginReservationInvitationGuest);
